import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, ResolveFn, Router, RouterStateSnapshot } from '@angular/router';
import { EMPTY, catchError, from, of, switchMap } from 'rxjs';
import { SlugService, SlugType } from '../services/slug.service';

/**
 * Resolves the `:id` route parameter — which may be a slug or a legacy
 * Firestore document id — into the real document id, and rewrites the URL to
 * the canonical slug when the visitor arrived through an id or an old alias.
 *
 * Components read the result from `route.data['entityId']`.
 */
export function entityIdResolver(type: SlugType): ResolveFn<string> {
  return (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    const param = route.paramMap.get('id');
    const router = inject(Router);
    const slugs = inject(SlugService);

    if (!param) return renderNotFound(router);

    return from(slugs.resolve(type, param)).pipe(
      switchMap(target => {
        if (!target) return renderNotFound(router);

        if (target.canonicalSlug && target.canonicalSlug !== param) {
          // Replaces the entry so the back button skips the id-based URL.
          router.navigate([routeBase(state.url), target.canonicalSlug], {
            queryParams: route.queryParams,
            fragment: route.fragment ?? undefined,
            replaceUrl: true
          });
          return EMPTY; // cancels this navigation in favour of the canonical one
        }

        return of(target.id);
      }),
      catchError(err => {
        // A denied read must not leave the router on a blank outlet.
        console.error('Failed to resolve the route parameter:', err);
        return renderNotFound(router);
      })
    );
  };
}

/** Keeps the URL intact while showing the wildcard "not found" component. */
function renderNotFound(router: Router) {
  router.navigate(['/not-found'], { skipLocationChange: true });
  return EMPTY;
}

function routeBase(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  return path.slice(0, path.lastIndexOf('/')) || '/';
}
